"use client";

import { dataLayer } from "@repo/analytics/data-layer-utils";
import Image from "next/image";
import React, { memo, useCallback, useMemo } from "react";
import {
  livelandingPageComponents,
  livelandingPageEventNames,
  livelandingPageEventTypes,
  livelandingPageJourneyNames,
} from "../../constants/livelanding-page.constants";
import { handleBothAppWebRedirection, handleRedirection } from "../../fireworks/fireworks";
import { ProductData } from "../../types/explore-top-products.types";
import {
  cleanProductTitle,
  extractEmiPrice,
  formatAmount,
  hasPositiveAmount,
} from "../../utils/productUtils";
import { getCtSectionTitle } from "../HeadlessPlaylistSection/headlessPlaylistUtils";

type ProductCardProps = {
  product: ProductData;
  origin?: "live" | "influencers";
  rightCardBgColor?: string;
  leftCardBgColor?: string;
  headingTextStyle?: string;
  borderColor?: string;
  fontWeight?: string;
  subtitleClass?: string;
  videoId?: string;
  sectionTitleCt?: string;
};

const safeOpenInNewTab = (url: string) => {
  if (!url) {
    return;
  }
  if (globalThis.window === undefined) {
    return;
  }

  try {
    if (url) {
      console.log("unitUrl", url?.includes("@"));
      if (url?.includes("@")) {
        handleBothAppWebRedirection(url);
      } else {
        handleRedirection(url);
      }
    }
  } catch (error) {
    console.error("Failed to open URL in new tab", error);
  }
};

const getDefaultStyles = (isLive: boolean) => ({
  rightCardBgColor: "h-[90px]! bg-[#4D4D4D]",
  leftCardBgColor: "bg-[#666666]",
  headingTextStyle: isLive
    ? "leading-[14px] text-[#FFFFFF] md:leading-[18px]"
    : "leading-[14px] font-medium text-[#FFFFFF] md:leading-[18px]",
  borderColor: "border border-[#999999]",
  fontWeight: "font-medium",
  subtitleClass: isLive ? "" : "text-[#CCCCCC]",
});

const getLayoutClasses = (
  isLive: boolean,
  borderColor: string,
  rightCardBgColor: string,
  leftCardBgColor: string
) => {
  const cardClassName = isLive
    ? `product-card relative flex w-[264px] shrink-0 cursor-pointer flex-row gap-1 overflow-hidden rounded-[2.94px] p-0 sm:h-[103px] sm:w-[270px] ${borderColor} ${rightCardBgColor}`
    : `relative m-0 box-border flex max-h-[90px] w-[264px] shrink-0 cursor-pointer flex-row overflow-hidden rounded-[3.16px] p-0 md:min-h-[103px] md:w-[270px] ${borderColor} ${rightCardBgColor}`;

  const imageContainerClassName = isLive
    ? `relative flex w-[84px] items-center justify-center rounded-l-[3px] sm:min-h-[103px] sm:min-w-[90.13px] ${leftCardBgColor}`
    : `relative m-0 box-border flex w-[84px] items-center justify-center md:h-[103px] ${leftCardBgColor}`;

  const detailsContainerClassName = isLive
    ? "product-details relative flex w-[160px] grow flex-col p-2"
    : "relative m-0 box-border flex w-[160px] grow flex-col p-[8px]";

  // Title: 11px mobile / 14px web — !important so parent headingTextStyle cannot override
  const headingClassName = isLive
    ? "product-title mb-0.5 line-clamp-2 overflow-hidden text-ellipsis font-medium !text-[11px] md:!text-[14px]"
    : "m-0 mb-[2px] box-border line-clamp-2 overflow-hidden text-ellipsis font-[500] !text-[11px] md:!text-[14px]";

  const emiTextSize = isLive
    ? "!text-[12px] font-normal leading-4 text-[#4D4D4D]"
    : "text-[12px] leading-4 font-normal text-[#4D4D4D] not-italic";

  // EMI price: 13px mobile / 16px web
  const emiPriceSize = "!text-[13px] leading-none text-[#089E86] md:!text-[16px]";

  const buttonContainerClassName = "mt-auto self-end";

  const buttonClassName = isLive
    ? "h-5 rounded-full border border-[#ff8900] bg-[#fff2ea] px-1.5 py-1 text-center text-[10px] uppercase leading-none text-[#ff6700] transition hover:opacity-80"
    : "m-0 box-border min-h-[20px] min-w-[77px] rounded-full border border-[#FF8900] bg-[#FFF2EA] px-1.5 py-1 text-center text-[10px] leading-none font-normal text-[#ff6700] uppercase not-italic transition hover:opacity-80";

  return {
    cardClassName,
    imageContainerClassName,
    detailsContainerClassName,
    headingClassName,
    emiTextSize,
    emiPriceSize,
    buttonContainerClassName,
    buttonClassName,
  };
};

const ProductBadge = ({ badge, isLive }: { badge?: string | null; isLive: boolean }) => {
  if (!badge) {
    return null;
  }

  const baseClassName =
    "font-rubik absolute top-0 left-0 flex h-5 items-center justify-center rounded-[2px] border border-white bg-[#b40000] px-1 text-center text-[10px] leading-none text-white overflow-hidden whitespace-nowrap text-ellipsis ";

  const className = isLive
    ? `${baseClassName} font-medium`
    : `${baseClassName} z-2 m-0 box-border font-medium font-normal not-italic`;

  return (
    <output aria-label={`Badge: ${badge}`} className={className}>
      {badge.substring(0, 12)}
      {badge.length > 12 && "..."}
    </output>
  );
};

const ProductImage = ({
  imageUrl,
  title,
  isLive,
}: {
  imageUrl: string;
  title: string;
  isLive: boolean;
}) => {
  if (imageUrl) {
    return (
      <Image
        src={imageUrl}
        alt={title || "Product image"}
        width={62}
        height={isLive ? 88 : 89}
        loading="lazy"
        decoding="async"
        className={"h-full w-full object-cover"}
      />
    );
  }

  if (isLive) {
    return null;
  }

  return (
    <div className="flex h-full w-full items-center justify-center text-xs text-white">
      No Image
    </div>
  );
};

const EmiSection = ({
  id,
  isLive,
  emiAmount,
  currencySymbol,
  emiTextSize,
  emiPriceSize,
  fontWeight,
  subtitleClass,
}: {
  id: ProductData["id"];
  isLive: boolean;
  emiAmount: string;
  currencySymbol: string;
  emiTextSize: string;
  emiPriceSize: string;
  fontWeight: string;
  subtitleClass: string;
}) => {
  const containerClassName = isLive
    ? "emi-info flex h-[21px] flex-row items-baseline gap-1 md:h-[18px]"
    : "m-0 mb-2 box-border flex h-[21px] flex-row items-baseline gap-1 md:h-[18px]";

  const labelClassName = `${isLive ? "emi-text" : "m-0 box-border"} ${emiTextSize} ${fontWeight} ${subtitleClass}`;
  const priceClassName = `${isLive ? "emi-price" : "m-0 box-border"} ${emiPriceSize} ${fontWeight}`;

  return (
    <div className={containerClassName}>
      <p className={labelClassName}>EMI starting at</p>
      <p id={`emi-price-${id}`} className={priceClassName}>
        {currencySymbol}
        {emiAmount}
      </p>
    </div>
  );
};

const ViewOffersCta = ({
  title,
  id,
  isLive,
  hasViewOfferLink,
  isCardClickable,
  onViewOffers,
  buttonContainerClassName,
  buttonClassName,
}: {
  title: string;
  id: ProductData["id"];
  isLive: boolean;
  hasViewOfferLink: boolean;
  isCardClickable: boolean;
  onViewOffers?: (e: React.MouseEvent) => void;
  buttonContainerClassName: string;
  buttonClassName: string;
}) => {
  const shouldShow = isLive || hasViewOfferLink;

  if (!shouldShow) {
    return null;
  }

  const sharedProps = {
    "aria-label": `View offers for ${title}`,
    "aria-describedby": `emi-price-${id}`,
    className: buttonClassName,
  };

  return (
    <div className={buttonContainerClassName}>
      {isCardClickable ? (
        <span {...sharedProps}>View Offers</span>
      ) : (
        <button type="button" onClick={onViewOffers} {...sharedProps}>
          View Offers
        </button>
      )}
    </div>
  );
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  origin = "influencers",
  rightCardBgColor,
  leftCardBgColor,
  headingTextStyle,
  borderColor,
  fontWeight,
  subtitleClass,
  sectionTitleCt,
}) => {
  const { id, product_name, product_images, product_units, productBadge, product_currency_symbol } =
    product;
  const ctSectionTitle = getCtSectionTitle(sectionTitleCt, undefined, "Explore Top Products");

  const imageUrl = useMemo(() => {
    return product_images?.[0]?.image_src || "";
  }, [product_images]);

  const viewOfferlink = useMemo(() => {
    return product_units?.[0]?.unit_url || "";
  }, [product_units]);

  const title = useMemo(() => {
    return cleanProductTitle(product_name || "");
  }, [product_name]);

  const emiPrice = useMemo(() => {
    return extractEmiPrice(product_name || "");
  }, [product_name]);

  const showEmi = hasPositiveAmount(emiPrice);
  const emiAmount = formatAmount(emiPrice);
  const currencySymbol = product_currency_symbol || "₹";

  const handleViewOffers = useCallback(() => {
    safeOpenInNewTab(viewOfferlink);
  }, [viewOfferlink]);

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleViewOffers();
    },
    [handleViewOffers]
  );

  const trackProductCardClick = useCallback(() => {
    safeOpenInNewTab(viewOfferlink);
    const eventName =
      origin === "influencers"
        ? livelandingPageEventNames.SOCIAL_COMMERCE_CLICKED
        : livelandingPageEventNames.LIVE_COMMERCE_PAGE_CLICKED;

    const data = {
      section_title: ctSectionTitle,
      component: livelandingPageComponents.EXPLORE_TOP_PRODUCTS,
      event: "PRODUCT_CARD_CLICK",
      element: "PRODUCT_CARD_CLICK",
      cta_text: title || "View Offers",
    };

    const map =
      typeof window !== "undefined"
        ? (window as unknown as { __fwVideoDescriptionMap?: Record<string, string> })
            .__fwVideoDescriptionMap || {}
        : {};

    const firstEntry = Object.entries(map)[0];
    const resolvedVideoId = typeof firstEntry?.[0] === "string" ? firstEntry[0] : "NA";
    const resolvedDescription = typeof firstEntry?.[1] === "string" ? firstEntry[1] : "NA";

    const cleverTapObject = {
      EVENT_NAME: eventName,
      EP_COMPONENT_NAME: livelandingPageComponents.EXPLORE_TOP_PRODUCTS,
      EP_SECTION: ctSectionTitle,
      EP_CTA: "PRODUCT_CLICK",
      EP_CTA_TEXT: "View Offers",
      EP_EVENT_TYPE: livelandingPageEventTypes.CLICK,
      EP_JOURNEY: livelandingPageJourneyNames.LIVE_COMMERCE,
      EP_JOURNEY_NAME: livelandingPageJourneyNames.LIVE_COMMERCE,
      EP_PRODUCT_TITLE: title || "NA",
      EP_PRODUCT_ID: String(id) || "NA",
      EP_PAGE_NAME: origin === "influencers" ? "INFLUENCER_PAGE" : "LIVE LANDING PAGE CLP",

      EP_PAGE_URL: typeof window !== "undefined" ? window.location.href : "NA",
      EP_PRODUCT_NAME: resolvedDescription || "NA",
      EP_PRODUCT_CATEGORY: `${resolvedDescription || "NA"} || ${resolvedVideoId || "NA"}`,
      EP_DESTINATION: viewOfferlink || "NA",
    };

    dataLayer("", data, cleverTapObject);
  }, [ctSectionTitle, title, id, viewOfferlink, origin]);

  const isLive = origin === "live";

  const defaults = getDefaultStyles(isLive);

  const finalRightCardBgColor = rightCardBgColor || defaults.rightCardBgColor;
  const finalLeftCardBgColor = leftCardBgColor || defaults.leftCardBgColor;
  const finalHeadingTextStyle = headingTextStyle || defaults.headingTextStyle;
  const finalBorderColor = borderColor || defaults.borderColor;
  const finalFontWeight = fontWeight || defaults.fontWeight;
  const finalSubtitleClass = subtitleClass || defaults.subtitleClass;

  const {
    cardClassName,
    imageContainerClassName,
    detailsContainerClassName,
    headingClassName,
    emiTextSize,
    emiPriceSize,
    buttonContainerClassName,
    buttonClassName,
  } = getLayoutClasses(isLive, finalBorderColor, finalRightCardBgColor, finalLeftCardBgColor);

  // headingClassName last so title sizes always win over headingTextStyle props
  const combinedHeadingClassName = `${finalHeadingTextStyle} ${headingClassName} text-left`;
  const combinedButtonClassName = `${buttonClassName} ${finalFontWeight}`;

  const hasViewOfferLink = !!viewOfferlink;
  const canOpenLink = isLive || hasViewOfferLink;

  const cardContent = (
    <>
      <div className={imageContainerClassName}>
        <ProductBadge badge={productBadge} isLive={isLive} />
        <ProductImage imageUrl={imageUrl} title={title} isLive={isLive} />
      </div>

      <div className={detailsContainerClassName}>
        <h2 className={combinedHeadingClassName}>{title}</h2>

        {showEmi ? (
          <EmiSection
            id={id}
            isLive={isLive}
            emiAmount={emiAmount}
            currencySymbol={currencySymbol}
            emiTextSize={emiTextSize}
            emiPriceSize={emiPriceSize}
            fontWeight={finalFontWeight}
            subtitleClass={finalSubtitleClass}
          />
        ) : null}

        <ViewOffersCta
          title={title}
          id={id}
          isLive={isLive}
          hasViewOfferLink={hasViewOfferLink}
          isCardClickable={canOpenLink}
          onViewOffers={handleButtonClick}
          buttonContainerClassName={buttonContainerClassName}
          buttonClassName={combinedButtonClassName}
        />
      </div>
    </>
  );

  if (canOpenLink) {
    return (
      <button
        className={cardClassName}
        onClick={(e) => {
          if (!viewOfferlink) {
            e.preventDefault();
          }
          trackProductCardClick();
        }}
        aria-label={`View offers for ${title}`}
      >
        {cardContent}
      </button>
    );
  }

  return <div className={cardClassName}>{cardContent}</div>;
};

const arePropsEqual = (prev: ProductCardProps, next: ProductCardProps): boolean => {
  return (
    prev.product.id === next.product.id &&
    prev.product.product_name === next.product.product_name &&
    prev.product.product_images === next.product.product_images &&
    prev.product.product_units === next.product.product_units &&
    prev.product.productBadge === next.product.productBadge &&
    prev.origin === next.origin &&
    prev.rightCardBgColor === next.rightCardBgColor &&
    prev.leftCardBgColor === next.leftCardBgColor &&
    prev.headingTextStyle === next.headingTextStyle &&
    prev.borderColor === next.borderColor &&
    prev.fontWeight === next.fontWeight &&
    prev.subtitleClass === next.subtitleClass
  );
};

export default memo(ProductCard, arePropsEqual);
